import { Check, X } from '@doist/icons/brand';
import { Heading, Text } from '@doist/marketist';
import cn from 'clsx';
import { useTranslation } from 'next-i18next';
import { useMatchMedia } from 'services/media-matcher';
import css from './pricing-table.module.css';
import type { ReactElement } from 'react';
import type { FeatureItem } from '../../feature-item/feature-item';
import type { PricingInfo } from '../../pricing-info/pricing-info';
type BodyRowProps = {
  tableHeader?: ReactElement<typeof FeatureItem>;
  hoverable?: boolean;
  children: ReactElement<typeof PricingTable.BodyCell>[];
};
type HeaderCellProps = {
  title?: string;
  size?: 'lg' | 'base' | 'xs';
  description?: string;
};
type CellProps = {
  data: string | boolean | number | ReactElement<typeof PricingInfo>;
};
type HeaderRowProps = {
  children: ReactElement<typeof PricingTable.HeaderCell>[];
};
type TableProps = {
  caption: string;
  tableBody: ReactElement<typeof PricingTable.Body>;
  headerRow?: ReactElement<typeof PricingTable.HeaderRow>;
  className?: string;
};
type TableBodyProps = {
  children: ReactElement<typeof PricingTable.BodyRow>[] | ReactElement<typeof PricingTable.BodyRow>;
};
function PricingTable({
  caption,
  headerRow,
  tableBody,
  className
}: TableProps) {
  return <table className={cn(css.table, className)} data-sentry-component="PricingTable" data-sentry-source-file="pricing-table.tsx">
            <caption>
                <Heading level="3" size="minor-lg" weight="semibold" data-sentry-element="Heading" data-sentry-source-file="pricing-table.tsx">
                    {caption}
                </Heading>
            </caption>
            {headerRow}
            {tableBody}
        </table>;
}
PricingTable.HeaderRow = function HeaderRow({
  children
}: HeaderRowProps) {
  return <thead>
            <tr>
                <th scope="col" />
                {children}
                {/* Additional empty column with zero width, so adding ::after on row hover won't change table layout */}
                <th scope="col" />
            </tr>
        </thead>;
};
PricingTable.HeaderCell = function HeaderCell({
  title,
  description,
  size
}: HeaderCellProps) {
  const isDesktop = useMatchMedia('(min-width: 960px)');
  return <th scope="col" className="pbe-8 pi-12">
            {!isDesktop || size === 'xs' ? <Text size="minor-sm" weight="semibold" className={cn(css.tertiary)}>
                    {title}
                </Text> : <>
                    <Text size={size === 'lg' ? 'major-sm' : 'minor-lg'}>{title}</Text>
                    <Text size="body-xs" weight="medium" className={cn(css.secondary, 'mbe-4 mbs-4')}>
                        {description}
                    </Text>
                </>}
        </th>;
};
PricingTable.Body = function TableBody({
  children
}: TableBodyProps) {
  return <tbody>{children}</tbody>;
};
PricingTable.BodyRow = function BodyRow({
  hoverable = true,
  tableHeader,
  children
}: BodyRowProps) {
  function handleRowMouseEnter(event: React.MouseEvent<HTMLTableRowElement>) {
    const row = event.currentTarget;
    row.style.setProperty('--height', `${row.clientHeight}px`);
  }
  return <tr onMouseEnter={handleRowMouseEnter} className={cn({
    [css.notHoverable]: !hoverable
  })}>
            <th scope="row">{tableHeader}</th>
            {children}
        </tr>;
};
PricingTable.BodyCell = function Cell({
  data
}: CellProps) {
  const {
    t,
    i18n
  } = useTranslation('pricing_v2_3');
  const fmt = new Intl.NumberFormat(i18n.language);
  const iconAvailable = <Check height="18" width="18" aria-label={t('comparison.iconLabels.available')} className={cn(css.icon, css.tertiary)} />;
  const iconNotAvailable = <X aria-label={t('comparison.iconLabels.notAvailable')} className={cn(css.icon, css.tertiary, css.visuallyHidden)} />;
  return <td>
            {typeof data === 'object' ? data : <Text size="body-xs" weight="medium">
                    {data === true && iconAvailable}
                    {data === false && iconNotAvailable}
                    {typeof data === 'string' && data}
                    {typeof data === 'number' && fmt.format(data)}
                </Text>}
        </td>;
};
export { PricingTable };